import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as CameraIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  EmojiEvents as TrophyIcon,
  Group as GroupIcon,
  Timeline as TimelineIcon,
  MonetizationOn as MoneyIcon,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { selectUser } from '../../store/slices/authSlice';
import { belarusRegions, regionsList, getCitiesByRegion } from '../../data/belarusRegions';
import DeleteAccountSection from '../../components/DeleteAccountSection';

export default function Profile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  
  const user = useSelector(selectUser);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [profileSurveys, setProfileSurveys] = useState([]);
  // Password change state
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMessage, setPwdMessage] = useState({ type: '', text: '' });
  
  // Состояния для показа/скрытия паролей
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Helper function to get full avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    const baseUrl = API_BASE.replace('/api', '');
    return `${baseUrl}${avatarPath}`;
  };

  // User data state
  const [userData, setUserData] = useState({
    fullName: '',
    email: '',
    phone: '',
    avatar: null
  });

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setProfileLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const profileData = await response.json();
        setUserData({
          fullName: profileData.fullName || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          avatar: profileData.avatar || null
        });
        // Sync localStorage so AppBar shows actual avatar after login
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        
        // Remove undefined/null values from profileData to prevent overwriting stored values
        const cleanProfileData = Object.fromEntries(
          Object.entries(profileData).filter(([_, v]) => v !== undefined && v !== null)
        );
        
        // Start with stored data, then update with fresh data from server
        // This ensures critical fields (id, role) are never lost
        const updatedUser = { 
          ...stored,
          ...cleanProfileData,
          // Explicitly ensure these critical fields are always present
          id: stored.id,
          role: stored.role
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        // Notify header in the same tab
        window.dispatchEvent(new Event('authChange'));
        // Keep Redux auth slice in sync
        dispatch({ type: 'auth/updateUser', payload: { fullName: profileData.fullName, email: profileData.email, phone: profileData.phone, avatar: profileData.avatar } });
      } else {
        console.error('Failed to load profile, status:', response.status);
        // Don't clear localStorage on error - user is still logged in
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const loadProfileCompletion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/surveys/profile/completion`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded profile completion:', data);
        setProfileCompletion(data);
      } else {
        console.error('Failed to load profile completion:', response.status);
      }
    } catch (error) {
      console.error('Error loading profile completion:', error);
    }
  };

  const loadProfileSurveys = async () => {
    try {
      const token = localStorage.getItem('token');
      // include completed profile surveys too
      const response = await fetch(`${API_BASE}/surveys?profileSurveysOnly=true&includeCompletedProfile=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const surveys = data.surveys || data;
        console.log('Loaded profile surveys:', surveys);
        setProfileSurveys(Array.isArray(surveys) ? surveys : []);
      } else {
        console.error('Failed to load profile surveys:', response.status);
      }
    } catch (error) {
      console.error('Error loading profile surveys:', error);
    }
  };

  useEffect(() => {
    loadProfile();
    // Only respondents need profile completion data
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const role = storedUser?.role || user?.role;
      if (role === 'respondent') {
        loadProfileCompletion();
        loadProfileSurveys();
      }
    } catch {}
  }, [dispatch]);

  const handleChangePassword = async () => {
    try {
      setPwdMessage({ type: '', text: '' });
      if (!pwdNew || pwdNew.length < 8) {
        setPwdMessage({ type: 'error', text: 'Пароль должен содержать минимум 8 символов' });
        return;
      }
      if (pwdNew !== pwdConfirm) {
        setPwdMessage({ type: 'error', text: 'Пароли не совпадают' });
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) {
        setPwdMessage({ type: 'error', text: 'Не удалось определить пользователя' });
        return;
      }
      setPwdSaving(true);
      const resp = await fetch(`${API_BASE}/users/me/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword: pwdCurrent, newPassword: pwdNew })
      });
      if (resp.ok) {
        setPwdMessage({ type: 'success', text: 'Пароль успешно изменён' });
        setPwdCurrent('');
        setPwdNew('');
        setPwdConfirm('');
      } else {
        const data = await resp.json().catch(() => ({}));
        setPwdMessage({ type: 'error', text: data.error || 'Не удалось изменить пароль' });
      }
    } catch (e) {
      setPwdMessage({ type: 'error', text: 'Ошибка при смене пароля' });
    } finally {
      setPwdSaving(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Мгновенное превью
      setProfileImage(URL.createObjectURL(file));
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('avatar', file);
      try {
        // 1. Загружаем файл
        const res = await fetch(`${API_BASE}/upload/avatar`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          // 2. Обновляем профиль пользователя
          const updateRes = await fetch(`${API_BASE}/users/me`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ avatar: data.avatarUrl })
          });
          if (updateRes.ok) {
            // Обновляем userData с новым аватаром
            setUserData((prev) => ({ ...prev, avatar: data.avatarUrl }));
            // Обнуляем превью, чтобы использовался путь из userData через getAvatarUrl
            setProfileImage(null);
            // Обновляем localStorage
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            storedUser.avatar = data.avatarUrl;
            localStorage.setItem('user', JSON.stringify(storedUser));
            // Обновляем AppBar в этой же вкладке
            window.dispatchEvent(new Event('authChange')); 
          } else {
            alert('Ошибка обновления профиля');
          }
        } else {
          alert('Ошибка загрузки фото');
        }
      } catch (e) {
        alert('Ошибка загрузки фото');
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить фото профиля?')) return;
    try {
      const token = localStorage.getItem('token');
      const updateRes = await fetch(`${API_BASE}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ avatar: null })
      });
      if (updateRes.ok) {
        setUserData((prev) => ({ ...prev, avatar: null }));
        setProfileImage(null);
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.avatar = null;
        localStorage.setItem('user', JSON.stringify(storedUser));
        // Обновляем AppBar в этой же вкладке
        window.dispatchEvent(new Event('authChange'));
      } else {
        alert('Ошибка удаления фото');
      }
    } catch (e) {
      alert('Ошибка удаления фото');
    }
  };


  const handleSave = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = storedUser?.id;
      
      if (!userId) {
        alert('Ошибка: пользователь не определен. Пожалуйста, войдите заново.');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        alert('Ошибка: токен авторизации не найден. Пожалуйста, войдите заново.');
        return;
      }

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Clean phone number - ensure it has proper format
      let cleanPhone = userData.phone;
      if (cleanPhone && !cleanPhone.startsWith('+')) {
        cleanPhone = '+' + cleanPhone;
      }

      const payload = {
        fullName: userData.fullName,
        phone: cleanPhone
      };
      
      const response = await fetch(`${API_BASE}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        // Get current user from localStorage to preserve all fields including role
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        // Update only the fields we changed, keeping role and other important fields
        const updatedUser = { 
          ...storedUser,
          fullName: userData.fullName,
          phone: cleanPhone
        };
        
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Update Redux store
        dispatch({ type: 'auth/updateUser', payload: userData });
        
        // Trigger auth change event to update header
        window.dispatchEvent(new Event('authChange'));
        
        setSaveSuccess(true);
        setEditMode(false);
        
        // Just reload the page data without navigation
        setTimeout(() => {
          loadProfile();
        }, 1000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server error:', response.status, errorData);
        alert(`Не удалось сохранить профиль: ${errorData.error || 'Ошибка сервера'}`);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(`Не удалось сохранить профиль: ${error.message}`);
    }
  };

  if (profileLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Get user role from Redux or localStorage
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user?.role || storedUser?.role;

  return (
    <Box>
      {/* Profile Surveys Completion Section */}
      {userRole === 'respondent' && (profileCompletion || profileSurveys.length > 0) && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, md: 4 },
            mx: { xs: 2, md: 3 },
            mb: 3,
            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            borderRadius: { xs: '8px', md: '32px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(9, 132, 227, 0.3)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: 'white'
              }}
            >
              Заполнение профиля
            </Typography>
            {profileCompletion && profileCompletion.totalProfileSurveys > 0 && (
              <Chip 
                label={`${profileCompletion.completedProfileSurveys}/${profileCompletion.totalProfileSurveys} завершено`}
                sx={{
                  background: profileCompletion.completedProfileSurveys === profileCompletion.totalProfileSurveys 
                    ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)'
                    : 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                  color: profileCompletion.completedProfileSurveys === profileCompletion.totalProfileSurveys 
                    ? 'white'
                    : '#2d3748',
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 2,
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
            )}
          </Box>
          
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2,
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 500,
              lineHeight: 1.6
            }}
          >
            {!profileCompletion || profileCompletion.completedProfileSurveys === 0 
              ? 'Пройдите обязательные профильные опросы, чтобы получить доступ к большему количеству опросов'
              : profileCompletion.completedProfileSurveys === profileCompletion.totalProfileSurveys
              ? 'Поздравляем! Вы завершили все профильные опросы'
              : `Осталось ${profileCompletion.remainingProfileSurveys} из ${profileCompletion.totalProfileSurveys} профильных опросов`
            }
          </Typography>

          {profileCompletion && profileCompletion.totalProfileSurveys > 0 && (
            <Box 
              sx={{ 
                mb: 3,
                p: 2.5,
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1.5,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              >
                Прогресс: {profileCompletion.completionPercentage}%
              </Typography>
              <Box 
                sx={{ 
                  width: '100%',
                  height: 10,
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Box sx={{ 
                  width: `${profileCompletion.completionPercentage}%`, 
                  height: '100%', 
                  background: 'linear-gradient(90deg, #56ab2f 0%, #2d7a1f 100%)',
                  transition: 'width 0.3s ease',
                  boxShadow: '0 2px 8px rgba(86, 171, 47, 0.5)'
                }} />
              </Box>
            </Box>
          )}

          {profileSurveys.length > 0 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 2,
                      fontWeight: 700,
                      color: 'white',
                      fontSize: '1rem'
                    }}
                  >
                    Незавершённые профильные опросы
                  </Typography>
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 2,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      '& .MuiAlert-icon': { color: '#0984e3' },
                      '& .MuiAlert-message': { color: '#2d3748', fontWeight: 600 }
                    }}
                  >
                    За первое прохождение каждого профильного опроса вы получите <strong>0.3 балла</strong>
                  </Alert>
                  <Grid container spacing={1}>
                    {profileSurveys.filter(s => !s.alreadyCompleted).map((survey) => (
                      <Grid item xs={12} key={survey.id}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => navigate(`/respondent/surveys/${survey.id}`)}
                          sx={{ 
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                            color: '#2d3748',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '12px',
                            py: 1.5,
                            boxShadow: '0 4px 15px rgba(253, 203, 110, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #fdcb6e 0%, #ffeaa7 100%)',
                              boxShadow: '0 6px 20px rgba(253, 203, 110, 0.4)'
                            }
                          }}
                        >
                          {survey.title}
                        </Button>
                      </Grid>
                    ))}
                    {profileSurveys.filter(s => !s.alreadyCompleted).length === 0 && (
                      <Alert 
                        severity="success" 
                        sx={{ 
                          width: '100%',
                          background: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '12px',
                          '& .MuiAlert-icon': { color: '#56ab2f' },
                          '& .MuiAlert-message': { color: '#2d3748', fontWeight: 600 }
                        }}
                      >
                        Все обязательные опросы этой категории завершены
                      </Alert>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 2,
                      fontWeight: 700,
                      color: 'white',
                      fontSize: '1rem'
                    }}
                  >
                    Завершённые профильные опросы (можно перепройти)
                  </Typography>
                  <Grid container spacing={1}>
                    {profileSurveys.filter(s => s.alreadyCompleted).map((survey) => (
                      <Grid item xs={12} key={survey.id}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => navigate(`/respondent/surveys/${survey.id}`)}
                          sx={{ 
                            justifyContent: 'flex-start', 
                            textAlign: 'left',
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            color: '#0984e3',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '12px',
                            py: 1.5,
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 4px 15px rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 1)',
                              boxShadow: '0 6px 20px rgba(255, 255, 255, 0.3)'
                            }
                          }}
                        >
                          {survey.title} — Пройден (перепройти)
                        </Button>
                      </Grid>
                    ))}
                    {profileSurveys.filter(s => s.alreadyCompleted).length === 0 && (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          width: '100%',
                          background: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '12px',
                          '& .MuiAlert-icon': { color: '#0984e3' },
                          '& .MuiAlert-message': { color: '#2d3748', fontWeight: 600 }
                        }}
                      >
                        Пока нет завершённых профильных опросов
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  '& .MuiAlert-icon': { color: '#0984e3' },
                  '& .MuiAlert-message': { color: '#2d3748', fontWeight: 600 }
                }}
              >
                Перепрохождение профильного опроса обновит соответствующие поля вашего профиля и может изменить доступные опросы.
              </Alert>
            </Box>
          )}
        </Paper>
      )}

  <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '8px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography 
            variant="h5"
            sx={{ 
              fontWeight: 700,
              color: '#2d3748'
            }}
          >
            Информация профиля
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={editMode ? <SaveIcon /> : <EditIcon />}
              onClick={editMode ? handleSave : () => setEditMode(true)}
              sx={{
                background: editMode 
                  ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)'
                  : 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                px: 3,
                boxShadow: editMode 
                  ? '0 4px 15px rgba(86, 171, 47, 0.3)'
                  : '0 4px 15px rgba(9, 132, 227, 0.3)',
                '&:hover': {
                  background: editMode 
                    ? 'linear-gradient(135deg, #2d7a1f 0%, #56ab2f 100%)'
                    : 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
                }
              }}
            >
              {editMode ? 'Сохранить' : 'Редактировать'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Profile Image Section */}
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box 
                sx={{ 
                  position: 'relative',
                  display: 'inline-block',
                  '&:hover .delete-overlay': {
                    opacity: editMode && userData.avatar ? 1 : 0
                  }
                }}
              >
                <Box
                  sx={{
                    width: 150,
                    height: 150,
                    margin: '0 auto',
                    mb: 2,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                    padding: '4px',
                    boxShadow: '0 4px 20px rgba(9, 132, 227, 0.3)'
                  }}
                >
                  <Avatar
                    src={profileImage || getAvatarUrl(userData.avatar)}
                    sx={{ 
                      width: '100%',
                      height: '100%',
                      border: '3px solid white'
                    }}
                  />
                </Box>
                {editMode && userData.avatar && (
                  <Box
                    className="delete-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      mb: 2,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(214, 48, 49, 0.8)'
                      }
                    }}
                    onClick={handleDeleteAvatar}
                  >
                    <DeleteIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                )}
              </Box>
              {editMode && (
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="icon-button-file"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="icon-button-file">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CameraIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        color: 'white',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '12px',
                        px: 3,
                        boxShadow: '0 4px 15px rgba(9, 132, 227, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
                        }
                      }}
                    >
                      Загрузить фото
                    </Button>
                  </label>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Полное имя"
                  value={userData.fullName}
                  disabled={!editMode}
                  onChange={(e) => setUserData({ ...userData, fullName: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-focused fieldset': {
                        borderColor: '#0984e3',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#0984e3',
                      fontWeight: 600
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={userData.email}
                  disabled
                  helperText="Email нельзя изменить"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Телефон"
                  value={userData.phone}
                  disabled
                  helperText="Телефон нельзя изменить"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Demographic fields removed from manual editing; now controlled solely via profile surveys */}
        </Grid>
      </Paper>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={6000}
        onClose={() => setSaveSuccess(false)}
      >
        <Alert 
          onClose={() => setSaveSuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Профиль успешно обновлён!
        </Alert>
      </Snackbar>

      {/* Password change section - available for all roles */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          mt: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '8px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3,
            fontWeight: 700,
            color: '#2d3748'
          }}
        >
          Изменить пароль
        </Typography>
        {pwdMessage.text && (
          <Alert 
            severity={pwdMessage.type === 'success' ? 'success' : 'error'} 
            sx={{ 
              mb: 2,
              borderRadius: '12px',
              '& .MuiAlert-icon': { 
                color: pwdMessage.type === 'success' ? '#56ab2f' : '#d63031' 
              },
              '& .MuiAlert-message': {
                color: '#2d3748',
                fontWeight: 600
              }
            }} 
            onClose={() => setPwdMessage({ type: '', text: '' })}
          >
            {pwdMessage.text}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type={showCurrentPassword ? 'text' : 'password'}
              label="Текущий пароль"
              value={pwdCurrent}
              onChange={(e) => setPwdCurrent(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0984e3',
                  fontWeight: 600
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type={showNewPassword ? 'text' : 'password'}
              label="Новый пароль"
              value={pwdNew}
              onChange={(e) => setPwdNew(e.target.value)}
              helperText="Минимум 8 символов"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0984e3',
                  fontWeight: 600
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label="Подтвердите новый пароль"
              value={pwdConfirm}
              onChange={(e) => setPwdConfirm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0984e3',
                  fontWeight: 600
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button 
              variant="contained" 
              onClick={handleChangePassword} 
              disabled={pwdSaving}
              sx={{
                background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.2,
                boxShadow: '0 4px 15px rgba(9, 132, 227, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
                },
                '&.Mui-disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
            >
              Сменить пароль
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Account deletion - respondents only (admins have their own profile page) */}
      {(user?.role || 'respondent') !== 'admin' && <DeleteAccountSection />}
    </Box>
  );
}